import { ContractRevision } from './contracts.service';
import { todayIso } from '../../shared/utils/iso-date.util';

/**
 * The revision a contract action should be seeded from: the one running today, or — when
 * none is — the latest one by start date.
 *
 * Both entry points into "Change to Basic" used to resolve this themselves and disagreed
 * (PR #42 review H4). The contract panel used a bare `findIndex` with no fallback and
 * papered over the miss with `priceListId: rev?.priceListId ?? 0`, which the dialog then
 * filtered on — no plan has price list 0, so the dialog dead-ended with "No Basic plan
 * available in this price list". The Cancel-Customer path had a fallback and worked. A main
 * contract that has not started yet is a first-class case here, so the divergence hit
 * exactly the contracts where the date rules matter most.
 *
 * The fallback sorts before taking the last element: `ContractsService.getRevisions` does
 * not sort (`contracts.service.ts:307-311`) and `ContractPanelComponent` re-sorts for
 * display, so `revisions[revisions.length - 1]` was picking whatever the API happened to
 * return last — for a future-dated contract that can be the wrong price list, hence the
 * wrong Basic plan.
 */
export function currentOrLatestRevision(
  revisions: ContractRevision[],
  today: string = todayIso(),
): ContractRevision | null {
  const active = revisions.find(
    (r) => r.startDate <= today && (r.endDate === null || r.endDate >= today),
  );
  if (active) return active;

  // No `.at(-1)` — this project's TS lib target predates it (TS2550).
  const byStart = [...revisions].sort((a, b) => a.startDate.localeCompare(b.startDate));
  return byStart.length ? byStart[byStart.length - 1] : null;
}
